import { Routes } from '@angular/router';
import { shellAccessGuard } from './core/guards/shell-access.guard';
import { roleGuard } from './core/guards/role.guard';
import { dataTypeGuard } from './core/guards/data-type.guard';
import { accessRestrictedGuard } from './core/guards/access-restricted.guard';
import { landingGuard } from './core/guards/landing.guard';
import { SigninComponent } from './features/signin/signin.component';
import { CallbackComponent } from './features/callback/callback.component';
import { devOnlyRoutes } from './dev-routes';
import {
  PRIVILEGED_ROLES,
  REPORTS_ROLES,
  DataPermission,
} from './core/user-context/user-context.model';
import { ORG_SWITCH_DATA, RERUN_GUARDS_ON_ORG_SWITCH } from './core/navigation/org-switch-reload';

export const routes: Routes = [
  { path: 'signin', component: SigninComponent, title: 'Sign In' },
  { path: 'callback', component: CallbackComponent, title: 'Signing In' },
  {
    path: 'unauthorized',
    title: 'Access Not Available',
    loadComponent: () =>
      import('./features/unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
  },
  ...devOnlyRoutes,

  // Authenticated application shell (lazy) — all feature modules lazy-load beneath it.
  {
    path: '',
    loadComponent: () =>
      import('./layout/app-shell/app-shell.component').then((m) => m.AppShellComponent),
    // shellAccessGuard runs session → FCRA/T&C → org STRICTLY in sequence. A plain
    // `[authGuard, fcraGuard, orgGuard]` array would run them concurrently (Angular
    // only prioritizes the return value, not side effects), letting the org modal
    // race the T&C modal on refresh — the composed guard forces the correct order.
    canActivate: [shellAccessGuard],
    // An org switch re-creates every page below the shell (OrgSwitchReloadService);
    // guarded children re-check the new org via RERUN_GUARDS_ON_ORG_SWITCH.
    data: { [ORG_SWITCH_DATA.reloadRoot]: true },
    children: [
      {
        path: 'employee',
        // A restricted Authorized User goes to their Session Audit Log; listed
        // first so its redirect wins over dataTypeGuard's /unauthorized.
        canActivate: [accessRestrictedGuard, dataTypeGuard],
        runGuardsAndResolvers: RERUN_GUARDS_ON_ORG_SWITCH,
        data: { dataType: DataPermission.EmployeeData },
        loadChildren: () =>
          import('./features/employee-monitoring/employee-monitoring.routes').then(
            (m) => m.EMPLOYEE_ROUTES,
          ),
      },
      {
        path: 'pre-hire',
        canActivate: [accessRestrictedGuard, dataTypeGuard],
        runGuardsAndResolvers: RERUN_GUARDS_ON_ORG_SWITCH,
        data: { dataType: DataPermission.PreHireData },
        loadChildren: () =>
          import('./features/pre-hire/pre-hire.routes').then((m) => m.PRE_HIRE_ROUTES),
      },
      // Session Audit Log is open to every signed-in role (FDD §7.2: admins — POC /
      // NABP Admin / NABP Staff — see all users, everyone else their own sessions), so
      // it's matched before the admin-only /admin branch. View Full Log inside it keeps
      // its own admin guard.
      {
        path: 'admin/audit-log',
        loadChildren: () =>
          import('./features/admin/session-audit-log/session-audit-log.routes').then(
            (m) => m.SESSION_AUDIT_LOG_ROUTES,
          ),
      },
      {
        path: 'admin',
        canActivate: [roleGuard],
        runGuardsAndResolvers: RERUN_GUARDS_ON_ORG_SWITCH,
        data: { requiredRoles: PRIVILEGED_ROLES },
        loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
      },
      // Reports is POC / NABP and Authorized Users with view/edit access; which report
      // tabs show follows the user's data types (ReportsComponent).
      {
        path: 'reports',
        canActivate: [roleGuard],
        runGuardsAndResolvers: RERUN_GUARDS_ON_ORG_SWITCH,
        data: { requiredRoles: REPORTS_ROLES },
        loadChildren: () =>
          import('./features/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
      },
      // Default page: the first one in the user's side nav (landingGuard always
      // redirects, so this route never activates).
      { path: '', pathMatch: 'full', canActivate: [landingGuard], children: [] },
    ],
  },

  // Legacy sign-in landing target — land users on the authenticated shell.
  { path: 'dashboard', redirectTo: '', pathMatch: 'full' },
  { path: '**', redirectTo: '' },
];
